import type { HomeGuideCardId } from "./homeGuideConfig";

export type HomeGuideState = {
  dismissed: boolean;
  completedCardIds: HomeGuideCardId[];
  lastOpenedCard?: HomeGuideCardId | null;
};

const PREFIX = "aireport.homeGuide";

export function homeGuideStorageKey(userId: string, workspaceId: string): string {
  const u = (userId || "anonymous").trim() || "anonymous";
  const w = (workspaceId || "default").trim() || "default";
  return `${PREFIX}.${u}.${w}`;
}

export function readHomeGuideState(userId: string, workspaceId: string): HomeGuideState {
  try {
    const raw = localStorage.getItem(homeGuideStorageKey(userId, workspaceId));
    if (!raw) return { dismissed: false, completedCardIds: [] };
    const parsed = JSON.parse(raw) as Partial<HomeGuideState>;
    const completed = Array.isArray(parsed.completedCardIds)
      ? (parsed.completedCardIds.filter((id): id is HomeGuideCardId => typeof id === "string") as HomeGuideCardId[])
      : [];
    return {
      dismissed: parsed.dismissed === true,
      completedCardIds: completed,
      lastOpenedCard: (parsed.lastOpenedCard as HomeGuideCardId) || null,
    };
  } catch {
    return { dismissed: false, completedCardIds: [] };
  }
}

export function writeHomeGuideState(userId: string, workspaceId: string, state: HomeGuideState): void {
  localStorage.setItem(homeGuideStorageKey(userId, workspaceId), JSON.stringify(state));
}

export function setHomeGuideDismissed(userId: string, workspaceId: string, dismissed: boolean): HomeGuideState {
  const next = { ...readHomeGuideState(userId, workspaceId), dismissed };
  writeHomeGuideState(userId, workspaceId, next);
  return next;
}

export function markHomeGuideCardCompleted(
  userId: string,
  workspaceId: string,
  cardId: HomeGuideCardId
): HomeGuideState {
  const current = readHomeGuideState(userId, workspaceId);
  const completedCardIds = current.completedCardIds.includes(cardId)
    ? current.completedCardIds
    : [...current.completedCardIds, cardId];
  const next: HomeGuideState = { ...current, completedCardIds, lastOpenedCard: cardId };
  writeHomeGuideState(userId, workspaceId, next);
  return next;
}
